import React, { useEffect, useRef, useState } from 'react';
import {
  tenderAwardeeService,
  TenderAwardeeSearchHit,
  TenderAwardee,
} from '../../services/tenderAwardeeService';
import AwardeeFormModal from './AwardeeFormModal';

export interface AwardeeSearchSelectProps {
  value: number | null;
  displayName: string;
  onChange: (awardee: TenderAwardeeSearchHit | null) => void;
  required?: boolean;
  disabled?: boolean;
  /** Form field (add tender) vs list filter */
  variant?: 'form' | 'filter';
  allowCreate?: boolean;
}

const AwardeeSearchSelect: React.FC<AwardeeSearchSelectProps> = ({
  value,
  displayName,
  onChange,
  required,
  disabled,
  variant = 'form',
  allowCreate = true,
}) => {
  const isFilter = variant === 'filter';
  const showCreate = allowCreate && !isFilter;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TenderAwardeeSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInitialName, setCreateInitialName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const hits = await tenderAwardeeService.search(query, 20);
        setResults(hits);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectHit = (hit: TenderAwardeeSearchHit) => {
    onChange(hit);
    setQuery('');
    setOpen(false);
  };

  const clearSelection = () => {
    onChange(null);
    setQuery('');
  };

  const showNoResults = open && !loading && query.trim() !== '' && results.length === 0;

  return (
    <div ref={containerRef} className="relative">
      <label
        className={
          isFilter
            ? 'block text-xs sm:text-sm font-medium text-gray-700 mb-1'
            : 'block text-[11px] font-medium text-gray-600 mb-0.5'
        }
      >
        {isFilter ? 'Tender Awardee' : `Awardee name${required ? ' *' : ''}`}
      </label>
      {value && displayName ? (
        <div
          className={
            isFilter
              ? 'flex items-center gap-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-gray-50'
              : 'flex items-center gap-1 border border-gray-300 rounded px-2 py-1 text-xs bg-gray-50'
          }
        >
          <span className="flex-1 truncate">{displayName}</span>
          {!disabled && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-gray-500 hover:text-gray-800 shrink-0"
              aria-label="Clear awardee"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <input
          type="text"
          disabled={disabled}
          required={required && !value}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={isFilter ? 'Search or all awardees…' : 'Search awardee…'}
          className={
            isFilter
              ? 'block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:bg-gray-100'
              : 'block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug disabled:bg-gray-100'
          }
          autoComplete="off"
        />
      )}
      {open && !value && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg text-xs"
        >
          {loading && <li className="px-3 py-2 text-gray-500">Searching…</li>}
          {isFilter && !loading && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-600 italic"
                onClick={() => {
                  clearSelection();
                  setOpen(false);
                }}
              >
                All awardees
              </button>
            </li>
          )}
          {!loading && results.map((hit) => (
            <li key={hit.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className="w-full text-left px-3 py-2 hover:bg-yellow-50"
                onClick={() => selectHit(hit)}
              >
                <span className="font-medium">{hit.name}</span>
                {hit.awardee_phone && (
                  <span className="text-gray-500 ml-1">· {hit.awardee_phone}</span>
                )}
              </button>
            </li>
          ))}
          {showNoResults && (
            <li className="px-3 py-2 text-gray-600">
              No match.
              {showCreate && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="text-yellow-700 font-medium underline"
                    onClick={() => {
                      setCreateInitialName(query.trim());
                      setShowCreateModal(true);
                      setOpen(false);
                    }}
                  >
                    Add new awardee
                  </button>
                </>
              )}
            </li>
          )}
          {!loading && query.trim() === '' && results.length === 0 && (
            <li className="px-3 py-2 text-gray-500">Type to search awardees</li>
          )}
        </ul>
      )}
      {showCreate && !value && !open && (
        <button
          type="button"
          className="mt-0.5 text-[11px] text-yellow-700 hover:underline"
          onClick={() => {
            setCreateInitialName('');
            setShowCreateModal(true);
          }}
        >
          Add new awardee
        </button>
      )}
      {showCreate && (
        <AwardeeFormModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          initialValues={{ name: createInitialName }}
          onSaved={(awardee: TenderAwardee) => {
            onChange({
              id: awardee.id,
              name: awardee.name,
              awardee_city: awardee.awardee_city,
              awardee_phone: awardee.awardee_phone,
              awardee_email: awardee.awardee_email,
            });
            setQuery('');
            setOpen(false);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

export default AwardeeSearchSelect;
